import { Address } from "src/app/backend/models/address";
import { QuoteItem } from "src/app/backend/models/quote/quote.item";
import { VatSummaryLine } from "./vat-summary-line";
import { FiscalProfileType } from "src/app/backend/models/fiscal-profile/fiscal-profile";
import { some } from "lodash";

export enum QuoteStatus {
  DRAFT = "draft",
  ACCEPTED = "accepted",
  REJECTED = "rejected",
  SIGNED = "signed",
  BILLED = "billed"
}

export enum CurrencyType {
  EURO = "euro"
}

export type QuoteCustomer = {
  id: string;
  type: "individual" | "business" | "public" | "association";
  firstName?: string;
  lastName?: string;
  businessName?: string;
  email?: string;
  phone?: string;
  ssn?: string;
  withoutSsn?: boolean;
  vatNumber?: string;
  address: {
    country?: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  iban?: string;
};

export interface QuoteAttachment {
  id: string;
  account: string;
  createdAt: string;
  createdBy: {
    id: string;
    name: string;
  };
  name: string;
  parentId?: string;
  size: number;
  status: string;
  storageId: string;
  tenant: string;
  type: string;
  updatedAt: string;

  tmpThumbnailUrl?: string;
}

export interface Quote {
  id: string;
  number: string;
  minMaxEnabled?: boolean;
  subject: string;
  description?: string;
  note?: string;
  currency: CurrencyType;
  assignee: {
    id: string;
    firstName: string;
    lastName: string;
  };
  status?: QuoteStatus;
  customer: QuoteCustomer;
  patient?: {
    id: string;
    number: string;
    name: string;
    microchip?: string;
    insuranceNumber?: string;
  };
  fiscalProfile: {
    id: string;
    name: string;

    type: FiscalProfileType;
    firstName?: string;
    lastName?: string;
    businessName?: string;
    vatNumber?: string;
    ssn?: string;
    email?: string;
    pec?: string;

    professionalRegister?: string;
    professionalRegisterProvince?: string;
    professionalRegisterNumber?: string;
    professionalRegisterSignedAt?: Date;
    professionalTitle?: string;
    eoriCode?: string;

    profession: string;
    taxRegime: "normal-regime" | "flat-tax-regime" | "facilitated-tax-system";
    withholdingTax: number;
    stampTax: number;

    address: Address;

    enpav?: {
      rate: number;
      drugs: boolean;
      products: boolean;
    };
  };
  items: QuoteItem[];
  expiresAt?: string;
  deliveredAt?: string;
  signable?: boolean;
  signedAt?: Date;
  acceptedAt?: string;
  acceptedBy?: {
    id: string;
    name: string;
  };

  rejectedAt?: string;
  rejectedBy?: {
    id: string;
    name: string;
  };

  billed?: boolean;
  itemsTotal: number;

  enpavRateableTotal: number; // Imponibile ENPAV

  enpavAmount: number; // Importo Enpav
  maxEnpavAmount: number; // Importo Enpav

  withholdingTaxRateableTotal: number; // Imponibile Ritenuta d'acconto
  maxWithholdingTaxRateableTotal?: number; // Imponibile Ritenuta d'acconto

  withholdingTaxAmount: number; // Importo Ritenuta d'acconto
  maxWithholdingTaxAmount?: number; // Importo Ritenuta d'acconto

  stampTaxAmount: number; // Imposta di bollo

  rateableTotal: number;
  maxRateableTotal?: number;

  vatTaxAmount: number;
  taxesAmount: number;
  taxedTotal: number;
  discount?: number;
  discountedTotal: number;
  adjustment?: number;

  adjustedTotal: number;
  maxAdjustedTotal: number;

  vatSummaryDetails: VatSummaryLine[];
  createdAt: string;
  createdBy?: {
    id: string;
    name: string;
  };
  notTaxableTotal?: number;
  discountAmount?: number;

  productsSaleTaxed: number;
  productsSaleMaxTaxed: number;

  drugsAssignmentTaxed: number;
  drugsAssignmentMaxTaxed: number;

  parapharmaceuticalTaxed: number;
  parapharmaceuticalMaxTaxed: number;

  medicalServicesTaxed: number;
  medicalServicesMaxTaxed: number;

  storageId?: string;

  recoveryId?: string;

  attachments?: QuoteAttachment[];

  billedAmount: number;
}

export type QuoteLight = Pick<
  Quote,
  | "number"
  | "rejectedAt"
  | "acceptedAt"
  | "status"
  | "signedAt"
  | "billed"
  | "createdAt"
>;

export interface QuoteCreate {
  subject: string;
  currency: CurrencyType;
  assignee: {
    id: string;
    firstName: string;
    lastName: string;
  };
  customer: {
    id: string;
    type: "individual" | "business" | "public" | "association";
    firstName?: string;
    lastName?: string;
    businessName?: string;
    email?: string;
    phone?: string;
    ssn?: string;
    vatNumber?: string;
    address: {
      country?: string;
      address?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    };
    iban?: string;
  };
  patient?: {
    id: string;
    number: string;
    name: string;
    microchip?: string;
    insuranceNumber?: string;
  };
  fiscalProfile: {
    id: string;
    name: string;

    type: FiscalProfileType;
    firstName?: string;
    lastName?: string;
    businessName?: string;
    vatNumber?: string;
    ssn?: string;
    email?: string;
    pec?: string;

    professionalRegister?: string;
    professionalRegisterProvince?: string;
    professionalRegisterNumber?: string;
    professionalRegisterSignedAt?: Date;
    professionalTitle?: string;
    eoriCode?: string;

    profession: string;
    taxRegime: "normal-regime" | "flat-tax-regime" | "facilitated-tax-system";
    enpav?: {
      rate: number;
      drugs: boolean;
      products: boolean;
    };
    withholdingTax: number;
    stampTax: number;

    address: Address;
    enableCommercialRounding: boolean;
  };
  recoveryId?: string;
}

export interface QuotePrintViewModel {
  businessName: string;
  businessAddress: string;
  businessVatCode: string;
  businessFiscalCode: string;

  quoteNumber: string;
  quoteDate: string;

  customerNominative: string;
  customerAddressLine1: string;
  customerAddressLine2: string;
  customerVatCode: string;
  customerFiscalCode: string;

  patientName: string;

  rows: Array<{
    description: string;
    quantity: string;
    maxQuantity: string;

    unitPrice: string;
    maxUnitPrice?: string;

    netTotal: string;
    maxNetTotal?: string;

    vat: string;
    discount: string;
  }>;

  vatSummaryLines: Array<{
    detail: {
      taxAmount: string;
      maxTaxAmount: string;
    };
    tax: string;
  }>;

  minMax?: boolean;

  discountedTotal: string;
  maxDiscountedTotal?: string;

  adjustedTotal: string;
  maxAdjustedTotal?: string;

  hideRateableTotal: boolean;
  notTaxableTotal: string;
  hideNotTaxableTotal: boolean;

  note: string;

  productsSaleTaxed: string;
  productsSaleMaxTaxed: string;

  drugsAssignmentTaxed: string;
  drugsAssignmentMaxTaxed: string;

  parapharmaceuticalTaxed: string;
  parapharmaceuticalMaxTaxed: string;

  medicalServicesTaxed: string;
  medicalServicesMaxTaxed: string;

  rateableTotal: string;
  maxRateableTotal?: string;

  taxedTotal: string;
  maxTaxedTotal?: string;

  adjustment: string;
  hideAdjustement: boolean;

  enpav: string;

  enpavAmount: string;
  maxEnpavAmount?: string;

  hideEnpavAmount: boolean;

  withholdingTax: string;

  withholdingTaxAmount: string;
  maxWithholdingTaxAmount?: string;

  hideWithholdingTaxAmount: boolean;

  flatRateProfile: boolean;

  footerName: string;

  preventivoLabel: string;
  dataLabel: string;
  patientLabel: string;
  spettabileLabel: string;
  pIvaLabel: string;
  cFLabel: string;
  descrizioneLabel: string;
  qtaLabel: string;
  qtaMinLabel: string;
  qtaMaxLabel: string;
  pZoLabel: string;
  pZoMin: string;
  pZoMax: string;
  totaleLabel: string;
  totMinLabel: string;
  totMaxLabel: string;
  scontoLabel: string;
  minimoLabel: string;
  massimoLabel: string;
  prestazioniLabel: string;
  prodottiLabel: string;
  farmaciLabel: string;
  parafarmaciLabel: string;
  imponibileLabel: string;
  nonImponibileLabel: string;
  ivaLabel: string;
  totalePreventivo: string;
  noteLabel: string;
  firmaPerAccettazione: string;
  firmaDigitaleDelClienteLabel: string;
  patientMicrochipLabel: string;
  patientLastWeightLabel: string;

  patientMicrochipValue: string;
  patientLastWeightValue: string;

  enableCustomerAddress: boolean;

  disableEnpavAmount: boolean;
  disableRitenutaDAcconto: boolean;
  disableFlatRateProfileNotice: boolean;
}

export function quoteIsAccepted(quote: Quote): boolean {
  return quote.status === "accepted";
}

export function quoteHasPatient(quote: Quote): boolean {
  return !!quote.patient;
}

export function quoteHasItems(quote: Quote): boolean {
  return (quote.items?.length || 0) > 0;
}

export function quoteNotAllItemHaveRunOutQuantity(quote: Quote): boolean {
  return some(quote.items, (item: QuoteItem) => {
    if (item.maxQuantity) {
      return (item.executedQuantity || 0) < item.maxQuantity;
    }

    return (item.executedQuantity || 0) > 0;
  });
}
